// src/components/LessonSummaryModal.jsx
import { useState, useEffect, useRef } from "react";

const SECTION_ICONS = { executive: "📋", topics: "🧠", actions: "✅" };
const RTL_LANGUAGES = ["ar", "he", "fa", "ur"];

// ── Helpers ─────────────────────────────────────────────────────
const isRTLCode = (code) => RTL_LANGUAGES.includes(code);

// Normalise language prop — always returns an array
const normaliseLangs = (language) =>
  Array.isArray(language) ? language.filter(Boolean) : [language].filter(Boolean);

// ── Claude API call via Firebase Cloud Function ─────────────────
async function generateSummary(transcriptText, language) {
  const response = await fetch(
    "https://us-central1-kings-app-74847.cloudfunctions.net/generateSummary",
    {
      method: "POST",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ transcriptText, language }),
    }
  );
  const data = await response.json();
  if (!response.ok) throw new Error(data.error || "Failed to generate summary.");
  return data;
}

// ── PDF download using browser print ───────────────────────────
function downloadAsPDF(summary, languages) {
  const html = buildPrintHTML(summary, languages);
  const win = window.open("", "_blank");
  win.document.write(html);
  win.document.close();
  win.focus();
  setTimeout(() => { win.print(); win.close(); }, 500);
}

// ── Plain text download ─────────────────────────────────────────
function downloadAsTXT(summary, isBilingual) {
  const buildSection = (s) => [
    s.title, s.date, "",
    "LESSON OVERVIEW", "─────────────────────────────────────", s.executive_summary, "",
    "KEY TOPICS", "─────────────────────────────────────",
    ...s.key_topics.map(t => `• ${t.topic}\n  ${t.detail}`), "",
    "ACTION ITEMS", "─────────────────────────────────────",
    ...s.action_items.map(a => `□ ${a}`),
  ].join("\n");

  let content;
  if (isBilingual && summary.secondary) {
    content = buildSection(summary.primary) + "\n\n" + "═".repeat(40) + "\n\n" + buildSection(summary.secondary);
  } else {
    content = buildSection(isBilingual ? summary.primary : summary);
  }

  const blob = new Blob([content], { type: "text/plain" });
  const a = document.createElement("a");
  a.href = URL.createObjectURL(blob);
  const title = isBilingual ? summary.primary.title : summary.title;
  a.download = `${title.replace(/\s+/g, "_")}_summary.txt`;
  a.click();
}

// ── HTML for print/PDF ──────────────────────────────────────────
// NOTE: This is the exported document, not on-screen UI — intentionally
// a light, King's Academy-branded print layout. Left unchanged.
function buildPrintHTML(summary, languages) {
  const langs = normaliseLangs(languages);
  const isBilingual = langs.length >= 2 && summary.secondary;
  const primary   = isBilingual ? summary.primary   : summary;
  const secondary = isBilingual ? summary.secondary : null;
  const primaryLang   = langs[0] || "en";
  const secondaryLang = langs[1] || "en";
  const primaryRTL   = isRTLCode(primaryLang);
  const secondaryRTL = isRTLCode(secondaryLang);

  const sectionHTML = (s, rtl) => `
    <div class="section" dir="${rtl ? "rtl" : "ltr"}" style="text-align:${rtl ? "right" : "left"}">
      <div class="section-label">Lesson Overview</div>
      <div class="overview" style="border-${rtl ? "right" : "left"}:4px solid #c8973a; border-${rtl ? "left" : "right"}:none; border-radius:${rtl ? "6px 0 0 6px" : "0 6px 6px 0"}">${s.executive_summary}</div>
    </div>
    <div class="section" dir="${rtl ? "rtl" : "ltr"}" style="text-align:${rtl ? "right" : "left"}">
      <div class="section-label">Key Topics</div>
      ${s.key_topics.map(t => `
        <div class="topic">
          <div class="topic-name">${t.topic}</div>
          <div class="topic-detail" style="padding-${rtl ? "right" : "left"}:12px; border-${rtl ? "right" : "left"}:2px solid #e8d5b0">${t.detail}</div>
        </div>
      `).join("")}
    </div>
    <div class="section" dir="${rtl ? "rtl" : "ltr"}" style="text-align:${rtl ? "right" : "left"}">
      <div class="section-label">Action Items</div>
      ${s.action_items.map(a => `
        <div class="action" style="flex-direction:${rtl ? "row-reverse" : "row"}">
          <div class="checkbox"></div><span>${a}</span>
        </div>
      `).join("")}
    </div>
  `;

  return `<!DOCTYPE html>
<html>
<head>
<meta charset="utf-8">
<title>${primary.title}</title>
<style>
  @import url('https://fonts.googleapis.com/css2?family=Lora:wght@400;600;700&family=Source+Sans+3:wght@400;600&display=swap');
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family:'Source Sans 3',sans-serif; color:#1a1a2e; background:#fff; padding:48px; max-width:${isBilingual ? "1100px" : "800px"}; margin:0 auto; }
  .header { border-bottom:3px solid #c8973a; padding-bottom:20px; margin-bottom:32px; }
  .school { font-size:11px; letter-spacing:0.15em; text-transform:uppercase; color:#c8973a; font-weight:600; margin-bottom:8px; }
  h1 { font-family:'Lora',serif; font-size:28px; color:#1a1a2e; line-height:1.3; margin-bottom:6px; }
  .date { font-size:13px; color:#666; }
  .columns { display:grid; grid-template-columns:1fr 1fr; gap:40px; }
  .column-label { font-size:10px; letter-spacing:0.15em; text-transform:uppercase; color:#c8973a; font-weight:700; margin-bottom:16px; padding-bottom:8px; border-bottom:1px solid #e8d5b0; }
  .section { margin-bottom:28px; }
  .section-label { font-size:10px; letter-spacing:0.15em; text-transform:uppercase; color:#c8973a; font-weight:700; margin-bottom:10px; }
  .overview { background:#f8f4ee; padding:16px 20px; font-size:15px; line-height:1.7; color:#2d2d2d; }
  .topic { margin-bottom:14px; }
  .topic-name { font-weight:600; font-size:15px; color:#1a1a2e; margin-bottom:3px; }
  .topic-detail { font-size:14px; color:#555; line-height:1.6; }
  .action { display:flex; align-items:flex-start; gap:10px; margin-bottom:10px; font-size:14px; color:#2d2d2d; line-height:1.5; }
  .checkbox { width:16px; height:16px; border:2px solid #c8973a; border-radius:3px; flex-shrink:0; margin-top:2px; }
  .footer { margin-top:40px; padding-top:16px; border-top:1px solid #e8d5b0; font-size:11px; color:#999; text-align:center; }
  @media print { body { padding:32px; } }
</style>
</head>
<body>
  <div class="header">
    <div class="school">King's Academy — Lesson Summary</div>
    <h1>${primary.title}</h1>
    <div class="date">${primary.date}</div>
  </div>
  ${isBilingual ? `
  <div class="columns">
    <div>
      <div class="column-label">${langs[0].toUpperCase()}</div>
      ${sectionHTML(primary, primaryRTL)}
    </div>
    <div>
      <div class="column-label">${langs[1].toUpperCase()}</div>
      ${sectionHTML(secondary, secondaryRTL)}
    </div>
  </div>
  ` : sectionHTML(primary, primaryRTL)}
  <div class="footer">Generated by King's Academy Faculty Tool Kit · k-a.app</div>
</body>
</html>`;
}

// ── Main Modal Component ────────────────────────────────────────
export default function LessonSummaryModal({ transcript, language, onClose, onSave, existingSummary }) {
  const langs       = normaliseLangs(language);
  const isBilingual = langs.length >= 2;
  const primaryLang   = langs[0] || "en";
  const secondaryLang = langs[1];
  const primaryRTL   = isRTLCode(primaryLang);
  const secondaryRTL  = isRTLCode(secondaryLang);

  const [summary, setSummary]               = useState(existingSummary || null);
  const [loading, setLoading]               = useState(!existingSummary);
  const [error, setError]                   = useState("");
  const [editing, setEditing]               = useState(false);
  const [edited, setEdited]                 = useState(existingSummary || null);
  const [copied, setCopied]                 = useState(false);
  const [saveStatus, setSaveStatus] = useState(existingSummary ? "saved" : "idle"); // idle | saved | failed
  const modalRef = useRef(null);

  useEffect(() => {
    if (existingSummary) return;
    console.log("Calling generateSummary, transcript length:", transcript?.length, "language:", language);
    generateSummary(transcript, language)
      .then(async (s) => {
        setSummary(s); setEdited(s); setLoading(false);
        if (onSave) {
          const ok = await onSave(s);
          setSaveStatus(ok ? "saved" : "failed");
        }
      })
      .catch(() => { setError("Failed to generate summary. Please try again."); setLoading(false); });
  }, []);

  useEffect(() => {
    const handleKey = (e) => { if (e.key === "Escape") onClose(); };
    window.addEventListener("keydown", handleKey);
    return () => window.removeEventListener("keydown", handleKey);
  }, [onClose]);

  const current = edited || summary;

  const handleSaveEdits = async () => {
    setEditing(false);
    if (onSave && edited) {
      const ok = await onSave(edited);
      setSaveStatus(ok ? "saved" : "failed");
    }
  };

  const handleCopy = () => {
    const buildText = (s) => [
      s.title, s.date, "",
      "OVERVIEW", s.executive_summary, "",
      "KEY TOPICS", ...s.key_topics.map(t => `${t.topic}: ${t.detail}`), "",
      "ACTION ITEMS", ...s.action_items.map(a => `□ ${a}`),
    ].join("\n");

    let text;
    if (isBilingual && current.secondary) {
      text = buildText(current.primary) + "\n\n---\n\n" + buildText(current.secondary);
    } else {
      text = buildText(isBilingual && current.primary ? current.primary : current);
    }
    navigator.clipboard.writeText(text);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  // ── Render a single summary column ──────────────────────────
  const renderColumn = (s, rtl, langCode, isSecondary = false) => {
    if (!s) return null;
    const rtlStyle = { direction: rtl ? "rtl" : "ltr", textAlign: rtl ? "right" : "left" };
    const borderSide = rtl ? "borderRight" : "borderLeft";

    const updateField = (field, value) => {
      if (isSecondary) setEdited({ ...current, secondary: { ...current.secondary, [field]: value } });
      else if (isBilingual) setEdited({ ...current, primary: { ...current.primary, [field]: value } });
      else setEdited({ ...current, [field]: value });
    };
    const updateTopic = (i, key, value) => {
      const src = isSecondary ? current.secondary : (isBilingual ? current.primary : current);
      const updated = [...src.key_topics];
      updated[i] = { ...updated[i], [key]: value };
      if (isSecondary) setEdited({ ...current, secondary: { ...current.secondary, key_topics: updated } });
      else if (isBilingual) setEdited({ ...current, primary: { ...current.primary, key_topics: updated } });
      else setEdited({ ...current, key_topics: updated });
    };
    const updateAction = (i, value) => {
      const src = isSecondary ? current.secondary : (isBilingual ? current.primary : current);
      const updated = [...src.action_items];
      updated[i] = value;
      if (isSecondary) setEdited({ ...current, secondary: { ...current.secondary, action_items: updated } });
      else if (isBilingual) setEdited({ ...current, primary: { ...current.primary, action_items: updated } });
      else setEdited({ ...current, action_items: updated });
    };

    return (
      <div style={{ flex: 1, minWidth: 0 }}>
        {/* Title */}
        <div style={{ marginBottom: "28px" }}>
          {editing ? (
            <input value={s.title} onChange={(e) => updateField("title", e.target.value)}
              style={{ ...editInputStyle, fontSize: "18px", fontWeight: "700", marginBottom: "8px", ...rtlStyle }} />
          ) : (
            <h2 style={{ fontSize: isBilingual ? "18px" : "24px", fontWeight: "700", color: "#0F172A", fontFamily: "Georgia, serif", marginBottom: "6px", lineHeight: 1.3, ...rtlStyle }}>
              {s.title}
            </h2>
          )}
          <div style={{ fontSize: "13px", color: "#B45309", letterSpacing: "0.06em", fontFamily: "'Courier New', monospace" }}>{s.date}</div>
        </div>

        {/* Executive Summary */}
        <SectionBlock icon={SECTION_ICONS.executive} label="Lesson Overview">
          {editing ? (
            <textarea value={s.executive_summary} onChange={(e) => updateField("executive_summary", e.target.value)} rows={4} style={{ ...editTextareaStyle, ...rtlStyle }} />
          ) : (
            <div style={{ background: "rgba(200,151,58,0.1)", [borderSide]: "3px solid #C8973A", padding: "16px 20px", borderRadius: rtl ? "8px 0 0 8px" : "0 8px 8px 0", color: "#334155", lineHeight: "1.75", fontSize: "14px", ...rtlStyle }}>
              {s.executive_summary}
            </div>
          )}
        </SectionBlock>

        {/* Key Topics */}
        <SectionBlock icon={SECTION_ICONS.topics} label="Key Topics Covered">
          {s.key_topics.map((t, i) => (
            <div key={i} style={{ marginBottom: "16px" }}>
              {editing ? (
                <div style={{ display: "flex", flexDirection: "column", gap: "6px" }}>
                  <input value={t.topic} onChange={(e) => updateTopic(i, "topic", e.target.value)} style={{ ...editInputStyle, fontWeight: "600", ...rtlStyle }} placeholder="Topic name" />
                  <textarea value={t.detail} onChange={(e) => updateTopic(i, "detail", e.target.value)} rows={2} style={{ ...editTextareaStyle, ...rtlStyle }} placeholder="Topic detail" />
                </div>
              ) : (
                <div style={{ paddingLeft: rtl ? 0 : "16px", paddingRight: rtl ? "16px" : 0, [borderSide]: "2px solid #CBD5E1" }}>
                  <div style={{ fontWeight: "600", color: "#0F172A", fontSize: "14px", marginBottom: "4px", ...rtlStyle }}>{t.topic}</div>
                  <div style={{ color: "#475569", fontSize: "13px", lineHeight: "1.65", ...rtlStyle }}>{t.detail}</div>
                </div>
              )}
            </div>
          ))}
          {editing && (
            <button onClick={() => {
              const src = isSecondary ? current.secondary : (isBilingual ? current.primary : current);
              const updated = [...src.key_topics, { topic: "", detail: "" }];
              if (isSecondary) setEdited({ ...current, secondary: { ...current.secondary, key_topics: updated } });
              else if (isBilingual) setEdited({ ...current, primary: { ...current.primary, key_topics: updated } });
              else setEdited({ ...current, key_topics: updated });
            }} style={addBtnStyle}>+ Add Topic</button>
          )}
        </SectionBlock>

        {/* Action Items */}
        <SectionBlock icon={SECTION_ICONS.actions} label="Action Items">
          {s.action_items.map((a, i) => (
            <div key={i} style={{ display: "flex", alignItems: "flex-start", gap: "12px", marginBottom: "10px", flexDirection: rtl ? "row-reverse" : "row" }}>
              <div style={{ width: "18px", height: "18px", borderRadius: "4px", border: "2px solid #C8973A", flexShrink: 0, marginTop: "2px" }} />
              {editing ? (
                <input value={a} onChange={(e) => updateAction(i, e.target.value)} style={{ ...editInputStyle, flex: 1, ...rtlStyle }} />
              ) : (
                <span style={{ color: "#334155", fontSize: "14px", lineHeight: "1.6", ...rtlStyle }}>{a}</span>
              )}
            </div>
          ))}
          {editing && (
            <button onClick={() => {
              const src = isSecondary ? current.secondary : (isBilingual ? current.primary : current);
              const updated = [...src.action_items, ""];
              if (isSecondary) setEdited({ ...current, secondary: { ...current.secondary, action_items: updated } });
              else if (isBilingual) setEdited({ ...current, primary: { ...current.primary, action_items: updated } });
              else setEdited({ ...current, action_items: updated });
            }} style={addBtnStyle}>+ Add Action Item</button>
          )}
        </SectionBlock>
      </div>
    );
  };

  // ── Determine what to render in the body ────────────────────
  const renderBody = () => {
    if (!current) return null;

    if (isBilingual && current.primary && current.secondary) {
      // Determine column order — RTL language always on the right
      const leftLang  = primaryRTL ? secondaryLang : primaryLang;
      const rightLang = primaryRTL ? primaryLang   : secondaryLang;
      const leftData  = primaryRTL ? current.secondary : current.primary;
      const rightData = primaryRTL ? current.primary   : current.secondary;
      const leftRTL   = isRTLCode(leftLang);
      const rightRTL  = isRTLCode(rightLang);
      const leftIsSecondary  = primaryRTL ? true  : false;
      const rightIsSecondary = primaryRTL ? false : true;

      return (
        <div style={{ display: "flex", gap: "28px", alignItems: "flex-start" }}>
          {/* Divider label left */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: "10px", color: "#B45309", letterSpacing: "0.12em", fontFamily: "'Courier New', monospace", marginBottom: "16px", paddingBottom: "8px", borderBottom: "1px solid #E2E8F0" }}>
              {leftLang.toUpperCase()}
            </div>
            {renderColumn(leftData, leftRTL, leftLang, leftIsSecondary)}
          </div>
          {/* Vertical divider */}
          <div style={{ width: "1px", background: "#E2E8F0", alignSelf: "stretch", flexShrink: 0 }} />
          {/* Divider label right */}
          <div style={{ flex: 1, minWidth: 0 }}>
            <div style={{ fontSize: "10px", color: "#B45309", letterSpacing: "0.12em", fontFamily: "'Courier New', monospace", marginBottom: "16px", paddingBottom: "8px", borderBottom: "1px solid #E2E8F0" }}>
              {rightLang.toUpperCase()}
            </div>
            {renderColumn(rightData, rightRTL, rightLang, rightIsSecondary)}
          </div>
        </div>
      );
    }

    // Single language
    return renderColumn(current, primaryRTL, primaryLang, false);
  };

  return (
    <div
      style={{ position: "fixed", inset: 0, zIndex: 1000, background: "rgba(15,23,42,0.45)", backdropFilter: "blur(6px)", display: "flex", alignItems: "center", justifyContent: "center", padding: "24px" }}
      onClick={(e) => { if (e.target === e.currentTarget) onClose(); }}
    >
      <div
        ref={modalRef}
        style={{
          width: "100%", maxWidth: isBilingual ? "1100px" : "780px", maxHeight: "90vh",
          background: "#FFFFFF", borderRadius: "16px", border: "1px solid #E2E8F0",
          display: "flex", flexDirection: "column", boxShadow: "0 24px 80px rgba(15,23,42,0.25)",
          animation: "slideUp 0.25s ease",
        }}
      >
        {/* Header */}
        <div style={{ padding: "20px 24px", borderBottom: "1px solid #E2E8F0", display: "flex", alignItems: "center", justifyContent: "space-between", flexShrink: 0 }}>
          <div style={{ display: "flex", alignItems: "center", gap: "12px" }}>
            <div style={{ width: "32px", height: "32px", borderRadius: "8px", background: "linear-gradient(135deg, #C8973A, #F59E0B)", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "16px" }}>📄</div>
            <div>
              <div style={{ fontWeight: "700", fontSize: "15px", color: "#0F172A", fontFamily: "'Courier New', monospace", letterSpacing: "0.04em" }}>
                LESSON SUMMARY{isBilingual ? " · BILINGUAL" : ""}
              </div>
              <div style={{ fontSize: "11px", color: "#64748B", letterSpacing: "0.08em", fontFamily: "'Courier New', monospace" }}>GENERATED BY CLAUDE AI</div>
            </div>
          </div>
          <div style={{ display: "flex", gap: "8px", alignItems: "center" }}>
            {!loading && saveStatus === "saved" && <div style={{ fontSize: "11px", color: "#16A34A", letterSpacing: "0.08em", fontFamily: "'Courier New', monospace", fontWeight: "600" }}>✓ SAVED TO HISTORY</div>}
            {!loading && saveStatus === "failed" && <div style={{ fontSize: "11px", color: "#B91C1C", letterSpacing: "0.08em", fontFamily: "'Courier New', monospace", fontWeight: "600" }}>⚠ NOT SAVED</div>}
            {!loading && summary && (
              <button onClick={editing ? handleSaveEdits : () => setEditing(true)} style={{ background: editing ? "rgba(200,151,58,0.15)" : "transparent", border: `1px solid ${editing ? "#C8973A" : "#CBD5E1"}`, borderRadius: "6px", padding: "6px 14px", color: editing ? "#B45309" : "#475569", cursor: "pointer", fontSize: "12px", fontFamily: "'Courier New', monospace", letterSpacing: "0.06em" }}>
                {editing ? "✓ SAVE EDITS" : "✏️ EDIT"}
              </button>
            )}
            <button onClick={onClose} style={{ background: "transparent", border: "1px solid #CBD5E1", borderRadius: "6px", padding: "6px 12px", color: "#64748B", cursor: "pointer", fontSize: "16px" }}>✕</button>
          </div>
        </div>

        {/* Body */}
        <div style={{ flex: 1, overflowY: "auto", padding: "28px 28px 8px" }}>
          {loading ? (
            <div style={{ textAlign: "center", padding: "60px 0" }}>
              <div style={{ fontSize: "40px", marginBottom: "16px", animation: "spin 2s linear infinite", display: "inline-block" }}>⚙️</div>
              <div style={{ color: "#475569", fontFamily: "'Courier New', monospace", fontSize: "14px" }}>
                {isBilingual ? "Generating bilingual lesson summary..." : "Generating lesson summary..."}
              </div>
              <div style={{ color: "#64748B", fontSize: "12px", marginTop: "8px", fontFamily: "'Courier New', monospace" }}>Claude is reading the transcript</div>
            </div>
          ) : error ? (
            <div style={{ background: "rgba(239,68,68,0.08)", border: "1px solid rgba(239,68,68,0.35)", borderRadius: "8px", padding: "16px", color: "#B91C1C", textAlign: "center" }}>{error}</div>
          ) : renderBody()}
        </div>

        {/* Download Bar */}
        {!loading && summary && (
          <div style={{ padding: "16px 24px", borderTop: "1px solid #E2E8F0", display: "flex", gap: "8px", flexShrink: 0, flexWrap: "wrap" }}>
            <div style={{ fontSize: "11px", color: "#64748B", alignSelf: "center", marginRight: "4px", fontFamily: "'Courier New', monospace", letterSpacing: "0.08em" }}>EXPORT:</div>
            <DownloadBtn onClick={() => downloadAsPDF(current, langs)} label="📄 PDF" />
            <DownloadBtn onClick={() => downloadAsTXT(current, isBilingual)} label="📝 TXT" />
            <DownloadBtn onClick={handleCopy} label={copied ? "✓ COPIED" : "📋 COPY"} highlight={copied} />
          </div>
        )}
      </div>

      <style>{`
        @keyframes slideUp { from { opacity:0; transform:translateY(20px); } to { opacity:1; transform:translateY(0); } }
        @keyframes spin { from { transform:rotate(0deg); } to { transform:rotate(360deg); } }
      `}</style>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────
function SectionBlock({ icon, label, children }) {
  return (
    <div style={{ marginBottom: "28px" }}>
      <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "14px" }}>
        <span style={{ fontSize: "14px" }}>{icon}</span>
        <div style={{ fontSize: "10px", fontWeight: "700", letterSpacing: "0.14em", textTransform: "uppercase", color: "#475569", fontFamily: "'Courier New', monospace" }}>{label}</div>
      </div>
      {children}
    </div>
  );
}

function DownloadBtn({ onClick, label, highlight }) {
  const [hovered, setHovered] = useState(false);
  return (
    <button onClick={onClick} onMouseEnter={() => setHovered(true)} onMouseLeave={() => setHovered(false)}
      style={{ padding: "8px 16px", borderRadius: "7px", border: `1px solid ${highlight ? "#C8973A" : hovered ? "#94A3B8" : "#CBD5E1"}`, background: highlight ? "rgba(200,151,58,0.12)" : hovered ? "#F1F5F9" : "transparent", color: highlight ? "#B45309" : "#475569", cursor: "pointer", fontSize: "12px", fontFamily: "'Courier New', monospace", letterSpacing: "0.06em", transition: "all 0.15s" }}>
      {label}
    </button>
  );
}

const editInputStyle = {
  width: "100%", background: "#FFFFFF", border: "1px solid #CBD5E1",
  borderRadius: "6px", padding: "8px 12px", color: "#1E293B",
  fontFamily: "Georgia, serif", fontSize: "13px", boxSizing: "border-box",
};
const editTextareaStyle = { ...editInputStyle, resize: "vertical", lineHeight: "1.6" };
const addBtnStyle = {
  background: "transparent", border: "1px dashed #CBD5E1", borderRadius: "6px",
  padding: "7px 14px", color: "#64748B", cursor: "pointer", fontSize: "12px",
  marginTop: "8px", fontFamily: "'Courier New', monospace", width: "100%",
};